import { useState, useEffect, useMemo, useRef } from 'react';
import { collection, onSnapshot, orderBy, query, limit } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { Dropdown } from '../components/ui';
import { HeaderSearch } from '../components/HeaderControls';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { usePaging } from '../hooks/usePaging';
import { useFitHeight } from '../hooks/useFitHeight';
import { Paging } from '../components/ui/Paging';
import { computeDateWindow, inWindow } from '../utils/ledgerReports';
import { downloadCSV } from '../utils/csv';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import type { ActivityAction, ActivityEntry, ActivityModule, Entity } from '../types';

// App Activity Log: who added, edited, deleted or split what, and when.
// Rows come from `activityLog`, written by utils/activity.ts inside the same
// transaction as each action: Purchase, Sales, Split & Merge, Backup, payments
// (receipts, supplier payments, broker payouts), Bank entries and companies.

const LOG_CAP = 2000;
const PERIODS = [
  { key: 'this-month', label: 'This Month' },
  { key: '3-months',   label: '3 Months' },
] as const;

const MODULE_LABEL: Record<ActivityModule, string> = {
  'purchase':    'Purchase',
  'sale':        'Sale',
  'split-merge': 'Split & Merge',
  'data':        'Backup',
  'receipt':     'Receipt',
  'payment':     'Supplier Payment',
  'broker':      'Broker Payout',
  'bank':        'Bank',
  'entity':      'Company',
  'revaluation': 'Stock Revaluation',
};
const ACTION_LABEL: Record<ActivityAction, string> = {
  create: 'Added',
  edit:   'Edited',
  delete: 'Deleted',
  split:  'Split',
  backup: 'Backed up',
  restore: 'Restored',
};
const ACTION_COLOR: Record<ActivityAction, string> = {
  create: 'var(--success)',
  edit:   'var(--primary)',
  delete: 'var(--danger)',
  split:  'var(--warning)',
  backup: 'var(--text-3)',
  restore: 'var(--danger)',
};

/** Local calendar date of an ISO time (the date filters work in local days). */
function localDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtWhen(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
  };
}

/** "PC-2026-0042" shows as "PC-0042" (prefix + last 4), same as the other
 *  list pages; the full number is in the tooltip. Split codes show as is. */
function lotNoDisplay(invoiceNo: string) {
  const m = invoiceNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : invoiceNo.slice(-4);
}

function ActionBadge({ action }: { action: ActivityAction }) {
  const c = ACTION_COLOR[action];
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
      color: c, background: `color-mix(in srgb, ${c} 12%, transparent)`,
      border: `1px solid color-mix(in srgb, ${c} 30%, transparent)`,
    }}>{ACTION_LABEL[action]}</span>
  );
}

function LotPill({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <span title={title} style={{
      display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
      fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 5, letterSpacing: '0.02em',
    }}>{children}</span>
  );
}

function UserChip({ name }: { name: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]!.toUpperCase()).join('') || '?';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text-1)', fontWeight: 600 }}>
      <span style={{
        width: 22, height: 22, borderRadius: 999, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--primary-light)', color: 'var(--primary)', fontSize: 9.5, fontWeight: 800,
      }}>{initials}</span>
      {name}
    </span>
  );
}

function IconExport() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1v8M4 6.5L7 9.5l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.5 10.5v1.5a1 1 0 001 1h9a1 1 0 001-1v-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconEmptySearch() {
  return <svg width="36" height="36" viewBox="0 0 14 14" fill="none">
    <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.1" />
    <path d="M9.5 9.5l2.5 2.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>;
}
function IconEmptyCalendar() {
  return <svg width="36" height="36" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.1" />
    <path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>;
}

export default function ActivityLogPage() {
  const settings = useSettings();
  const { business } = useAuth();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [rows, setRows] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(() => !!getClientDb());
  // Why the log couldn't be read (e.g. the database refused), shown instead of an empty page.
  const [loadError, setLoadError] = useState('');
  const [entities, setEntities] = useState<Entity[]>([]);

  // Only This Month or 3 Months: the log itself keeps 3 months (utils/activity.ts pruneActivityLog).
  const [dateFilter, setDateFilter] = useState<'this-month' | '3-months'>('this-month');
  const [searchQ, setSearchQ] = useState('');
  const [printAll, setPrintAll] = useState(false);

  // Live, newest first. One orderBy on one field, so no composite index.
  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    return onSnapshot(
      query(collection(db, 'activityLog'), orderBy('at', 'desc'), limit(LOG_CAP)),
      snap => { setRows(snap.docs.map(d => ({ id: d.id, ...d.data() } as ActivityEntry))); setLoadError(''); setLoading(false); },
      err => { console.error('Activity Log listener error:', err); setLoadError(err?.message || 'Could not read the Activity Log.'); setLoading(false); },
    );
    // Re-subscribe once the company database is connected (business set).
  }, [business]);

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    return onSnapshot(collection(db, 'entities'), snap => {
      setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity)));
    });
  }, [business]);

  const entityName = (id?: string) => id ? (entities.find(e => e.id === id)?.companyName ?? '') : '';

  const dateRange = useMemo(() => computeDateWindow(dateFilter, '', ''), [dateFilter]);

  const baseRows = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return rows.filter(r => {
      if (!inWindow(localDate(r.at), dateRange ?? undefined)) return false;
      if (q) {
        const hay = [r.recordNo, r.userName, entityName(r.partyId), r.note].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, dateRange, searchQ, entities]);

  // Excel-style filters on User, Action, Type and Party (the user list
  // comes from the log itself: a staff login can't read other users' records).
  const filterColumns = useMemo<FilterColumn<ActivityEntry>[]>(() => [
    { key: 'user',   label: 'User',   get: r => r.userName || 'Unknown user' },
    { key: 'action', label: 'Action', get: r => ACTION_LABEL[r.action] },
    { key: 'type',   label: 'Type',   get: r => MODULE_LABEL[r.module] },
    { key: 'party',  label: 'Party',  get: r => entityName(r.partyId) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entities]);
  const { rows: filtered, filters: colFilters } = useColumnFilters(baseRows, filterColumns);
  const paging = usePaging(filtered, `${searchQ}|${dateFilter}|${colFilters.stamp}`);
  const wrapRef = useRef<HTMLDivElement>(null);
  useFitHeight(wrapRef, 56, 280, [filtered.length > 0, loading]);


  useEffect(() => {
    if (!printAll) return;
    let cancelled = false;
    const after = () => setPrintAll(false);
    window.addEventListener('afterprint', after);
    requestAnimationFrame(() => {
      if (cancelled) return;
      requestAnimationFrame(() => { if (!cancelled) window.print(); });
    });
    return () => { cancelled = true; window.removeEventListener('afterprint', after); };
  }, [printAll]);

  const displayedRows = printAll ? filtered : paging.pageRows;

  /** "3 items · 12.50 ct · $12,400", plus "was …" on an edit when it changed. */
  function details(r: ActivityEntry): { now: string; was: string } {
    const part = (n?: number, ct?: number, amt?: number) => [
      n != null ? `${n} ${r.module === 'split-merge' ? (n === 1 ? 'cut' : 'cuts') : (n === 1 ? 'item' : 'items')}` : '',
      ct != null ? `${ct.toFixed(2)} ct` : '',
      amt != null ? money(amt) : '',
    ].filter(Boolean).join(' · ');
    const now = part(r.itemCount, r.carat, r.amountUSD);
    const changed = r.action === 'edit' && (
      r.prevItemCount !== r.itemCount
      || Math.abs((r.prevCarat ?? 0) - (r.carat ?? 0)) > 1e-6
      || Math.abs((r.prevAmountUSD ?? 0) - (r.amountUSD ?? 0)) > 0.005
    );
    return { now, was: changed ? part(r.prevItemCount, r.prevCarat, r.prevAmountUSD) : '' };
  }

  function handleExportCSV() {
    downloadCSV(
      'activity-log',
      ['Date', 'Time', 'User', 'Action', 'Type', 'Record', 'Party', 'Details', 'Before', 'Note'],
      filtered.map(r => {
        const w = fmtWhen(r.at);
        const d = details(r);
        return [w.date, w.time, r.userName, ACTION_LABEL[r.action], MODULE_LABEL[r.module],
          r.recordNo ?? '', entityName(r.partyId), d.now, d.was, r.note ?? ''];
      }),
    );
  }

  const muted = <span style={{ opacity: 0.4 }}>—</span>;
  const cell = { fontSize: 12, color: 'var(--text-2)' } as const;

  return (
    <AppLayout
      title="Activity Log"
      subtitle="Who added, edited, deleted or split what, and when."
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Search record, user, party…" label="Search activity" />
        <div className="tb-seg" role="tablist" aria-label="Period">
          {PERIODS.map(p => (
            <button key={p.key} type="button" role="tab" aria-selected={dateFilter === p.key}
              className={dateFilter === p.key ? 'on' : undefined} onClick={() => setDateFilter(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
        {(searchQ || dateFilter !== 'this-month' || colFilters.active.length > 0) && (
          <button type="button" className="tb-clear" onClick={() => {
            setSearchQ(''); setDateFilter('this-month'); colFilters.clear();
          }}>Clear</button>
        )}
      </>}
      headerActions={
        <div className="tb-group">
          <Dropdown
            align="right"
            trigger={() => (
              <button type="button" className="tb-group-btn" title="Export" aria-label="Export"><IconExport /></button>
            )}
          >
            <button type="button" className="dropdown-item" onClick={handleExportCSV}>Export CSV</button>
            <button type="button" className="dropdown-item" onClick={() => setPrintAll(true)}>Print List</button>
          </Dropdown>
        </div>
      }
    >
      {colFilters.active.length > 0 && (
        <div className="no-print" style={{ display: 'flex', marginBottom: 8 }}>
          <ColumnFilterChips filters={colFilters} inline />
        </div>
      )}
      {loadError && (
        <div className="msg msg-error" style={{ marginBottom: 12 }}>
          The Activity Log could not be loaded: {loadError}
        </div>
      )}
      {loading ? (
        <div className="table-wrap">
          <table>
            <thead><tr>{['When', 'User', 'Action', 'Type', 'Record', 'Party', 'Details'].map(h => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>{[...Array(6)].map((_, i) => (
              <tr key={i} style={{ opacity: 1 - i * 0.14 }}>
                {[...Array(7)].map((_, j) => <td key={j}><div className="skeleton" style={{ height: 13, width: '80%', borderRadius: 4 }} /></td>)}
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'var(--text-3)' }}>
            {rows.length === 0 ? <IconEmptyCalendar /> : <IconEmptySearch />}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>
            {rows.length === 0 ? 'No activity yet' : 'Nothing matches your filters'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', maxWidth: 340, margin: '0 auto' }}>
            {rows.length === 0
              ? 'Sales, purchases, payments, broker payouts, bank entries and company changes show up here with who did them.'
              : 'Try a wider date range or clear a filter.'}
          </div>
        </div>
      ) : (
        <>
        <div ref={wrapRef} className="table-wrap table-fit table-fit-stats">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th className="th-filterable">User<ColumnFilterButton filters={colFilters} colKey="user" label="User" /></th>
                <th className="th-filterable">Action<ColumnFilterButton filters={colFilters} colKey="action" label="Action" /></th>
                <th className="th-filterable">Type<ColumnFilterButton filters={colFilters} colKey="type" label="Type" /></th>
                <th style={{ minWidth: 150 }}>Record</th>
                <th className="th-filterable">Party<ColumnFilterButton filters={colFilters} colKey="party" label="Party" /></th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {displayedRows.map(r => {
                const w = fmtWhen(r.at);
                const d = details(r);
                const party = entityName(r.partyId);
                return (
                  <tr key={r.id}>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      {w.date}<span style={{ color: 'var(--text-3)', marginLeft: 6, fontFamily: 'var(--font-num)' }}>{w.time}</span>
                    </td>
                    <td><UserChip name={r.userName || 'Unknown user'} /></td>
                    <td><ActionBadge action={r.action} /></td>
                    <td style={cell}>{MODULE_LABEL[r.module]}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {!r.recordNo ? muted
                        : r.module === 'entity' ? <span style={{ ...cell, color: 'var(--text-1)', fontWeight: 600 }}>{r.recordNo}</span>
                        : <LotPill title={r.recordNo}>{r.module === 'split-merge' ? r.recordNo : lotNoDisplay(r.recordNo)}</LotPill>}
                    </td>
                    <td style={cell}>{party || muted}</td>
                    <td style={{ ...cell, fontFamily: 'var(--font-num)' }}>
                      {/* A line with only a note (Bank, Company) shows the note as its text. */}
                      {d.now || (r.note ? <span style={{ fontFamily: 'inherit' }}>{r.note}</span> : muted)}
                      {d.was && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>Before: {d.was}</div>}
                      {r.note && d.now && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{r.note}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!printAll && <Paging page={paging.page} totalPages={paging.totalPages} total={paging.total} onChange={paging.setPage} noun="action" />}
        </>
      )}
    </AppLayout>
  );
}
